import * as React from 'react';

import { FilterContext } from '../filter-bar/FilterContext';
import FilterInterstitial from '../search-result-page/FilterInterstitial';
import { useFormikContext } from 'formik';
import { FormikValues } from './FilterBar';
import { CheckboxSetField } from './filters/CheckboxSetField';
import { RoomsFilter } from './filters/RoomsFilter';
import { RadiusFilter } from './filters/RadiusFilter';
import { CSNAmenity, CSNAreaType } from '@weddingspot/ws-api-client';
import { useContext, useState } from 'react';
import { ShoppingCartContext, useOutsideClickHandler } from '@weddingspot/ws-core-components';

const _styles = require('./Filters.scss');

export const Filters = () => {
    const { selectedFilter, setSelectedFilter } = React.useContext(FilterContext);

    const { setFieldValue, setFieldTouched, resetForm, values } = useFormikContext<FormikValues>();
    const [radiusValue, setRadiusValue] = useState(60);
    const shoppingCardContext = useContext(ShoppingCartContext);
    const isVisible = !shoppingCardContext.cartOpen;
    const filtersRef = React.useRef<HTMLDivElement>(null);

    const onRadiusClose = () => {
        setFieldValue('radius', radiusValue);
        onDismiss();
    };

    const onDismiss = () => {
        setSelectedFilter(null);
    };

    useOutsideClickHandler(filtersRef, () => {
        if (values.radius !== radiusValue) {
            setFieldValue('radius', radiusValue);
        }
        setSelectedFilter(null);
    });

    const clearField = (name: string) => {
        setTimeout(() => {
            setFieldValue(name, undefined);
            setFieldTouched(name, false);
        });
    };

    const roomsFilter = (
        <RoomsFilter
            isVisible={selectedFilter === 'Guest Rooms' || selectedFilter === 'Filters'}
            min={0}
            max={null}
            onChange={(values) => {
                setFieldValue('rooms', values);
                setFieldTouched('rooms', true);
            }}
        />
    );

    const amenitiesFilter = (
        <CheckboxSetField
            name={'amenities'}
            options={[
                [CSNAmenity.CONCIERGE_SERVICES, 'Concierge services'],
                [CSNAmenity.INTERNET, 'Internet access'],
                [CSNAmenity.ROOM_SERVICE, 'Room service'],
                [CSNAmenity.LAUNDRY, 'Laundry service'],
                [CSNAmenity.STREET_PARKING, 'Street parking'],
                [CSNAmenity.COMPLIMENTARY_PARKING_PROVIDED, 'Complimentary parking'],
                [CSNAmenity.BUSINESS_CENTER, 'Business center'],
                [CSNAmenity.FREE_AIRPORT_SHUTTLE, 'Airport shuttle'],
                [CSNAmenity.HEALTH_CLUB, 'Health club'],
            ]}
        />
    );

    const areasFilter = (
        <CheckboxSetField
            name={'areas'}
            options={[
                [CSNAreaType.AIRPORT, 'Airport'],
                [CSNAreaType.INTERSTATE, 'Interstate'],
                [CSNAreaType.RESORT, 'Resort'],
                [CSNAreaType.SMALL_METRO_TOWN, 'Small metro or town'],
                [CSNAreaType.SUBURBAN, 'Suburban'],
                [CSNAreaType.URBAN, 'Urban'],
            ]}
        />
    );

    const radiusFilter = (
        <RadiusFilter
            isVisible={selectedFilter === 'Radius' || selectedFilter === 'Filters'}
            defaultValue={60}
            value={radiusValue}
            onChange={(value) => {
                setRadiusValue(value);
            }}
        />
    );

    return (
        <div ref={filtersRef}>
            <FilterInterstitial
                visible={isVisible && selectedFilter === 'Guest Rooms'}
                title='Guest Rooms'
                close={onDismiss}
                hideSecondaryDismiss={true}
                filterClassName={_styles.filter}
                bodyClassName={_styles.filterBody}
                clear={() => clearField('rooms')}
            >
                {roomsFilter}
            </FilterInterstitial>
            <FilterInterstitial
                visible={isVisible && selectedFilter === 'Amenities'}
                title='Amenities'
                close={onDismiss}
                hideSecondaryDismiss={true}
                filterClassName={_styles.filter}
                bodyClassName={_styles.filterBody}
                clear={() => clearField('amenities')}
            >
                {amenitiesFilter}
            </FilterInterstitial>
            <FilterInterstitial
                visible={isVisible && selectedFilter === 'Areas'}
                title='Areas'
                close={onDismiss}
                hideSecondaryDismiss={true}
                filterClassName={_styles.filter}
                bodyClassName={_styles.filterBody}
                clear={() => clearField('areas')}
            >
                {areasFilter}
            </FilterInterstitial>
            <FilterInterstitial
                visible={isVisible && selectedFilter === 'Radius'}
                title='Radius'
                close={onRadiusClose}
                hideSecondaryDismiss={true}
                filterClassName={_styles.filter}
                bodyClassName={_styles.filterBody}
                clear={() => {
                    setRadiusValue(0);
                }}
            >
                {radiusFilter}
            </FilterInterstitial>

            <FilterInterstitial
                visible={isVisible && selectedFilter === 'Filters'}
                title='Filters'
                close={onDismiss}
                hideSecondaryDismiss={false}
                filterClassName={_styles.filter}
                bodyClassName={_styles.filterBody}
                clear={() => {
                    resetForm();
                    // reset not clear form, instead set all value to initialValue,
                    // which is not correct for rooms (because initialValue taken from location.state)
                    clearField('rooms');
                }}
            >
                <div className={_styles.inlineFilterWrapper}>
                    <div className={_styles.filterTitle}>Guests</div>
                    <div className={_styles.inlineFilter}>{roomsFilter}</div>
                </div>
                <div className={_styles.inlineFilterWrapper}>
                    <div className={_styles.filterTitle}>Radius</div>
                    <div className={_styles.inlineFilter}>{radiusFilter}</div>
                </div>
                <div className={_styles.inlineFilterWrapper}>
                    <div className={_styles.filterTitle}>Amenities</div>
                    <div className={_styles.inlineFilter}>{amenitiesFilter}</div>
                </div>
                <div className={_styles.inlineFilterWrapper}>
                    <div className={_styles.filterTitle}>Areas</div>
                    <div className={_styles.inlineFilter}>{areasFilter}</div>
                </div>
            </FilterInterstitial>
        </div>
    );
};
